<template>
  <div class="product-details">
    <fn-accordion>
      <fn-accordion-item
        id="productDetails"
        ref="detailedProductDetails"
        :headline="$t('product.productDetails')"
        :active="activeTab === 'detailedProductDetails'"
        :disable-scroll-to-item="mobileActive"
        @toggle="changeTab($event, 'detailedProductDetails')"
      >
        <fn-headline
          type="div"
          size="h5"
        >
          <fn-link
            v-if="product.brandUrl"
            id="pdp-brand-link"
            color="black"
            no-arrow
            no-hover
            large
            :to="product.brandUrl"
            target="_blank"
          >
            {{ plpBrandFullName }}
          </fn-link>

          <template v-else>
            {{ plpBrandFullName }}
          </template>
        </fn-headline>
        <p>{{ $t('product.code') }} {{ product.code }}</p>

        <div class="paragraph">
          <div v-html="product.summary" />
        </div>
        <div class="paragraph">
          <div v-html="product.description" />
        </div>

        <fn-headline
          v-if="product.descriptionTable?.headline"
          type="h4"
          size="h5"
        >
          {{ product.descriptionTable.headline }}
        </fn-headline>

        <div
          v-if="product.descriptionTable?.table"
          class="pd-table"
        >
          <table>
            <tbody>
              <tr
                v-for="(item, rowIndex) in product.descriptionTable.table"
                :key="rowIndex"
              >
                <td
                  v-for="(value, columnIndex) in item"
                  :key="columnIndex"
                >
                  {{ value }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="product.additionalInformation"
          class="paragraph"
          v-html="product.additionalInformation"
        />

        <div
          v-if="product.warnings?.length"
          class="paragraph"
        >
          <div
            v-for="(warning, index) in productWarnings"
            :key="`warning${index}`"
            v-html="$t(`product.warnings.${warning}`)"
          />
        </div>

        <template v-if="product.biocide?.precautionDescriptions">
          <fn-headline
            ref="detailedProductDetailsBiocide"
            type="h4"
            size="h5"
            no-margin-bottom
          >
            {{ $t('product.biocides.storage') }}
          </fn-headline>

          <div
            class="paragraph"
            v-html="product.biocide?.precautionDescriptions"
          />
        </template>

        <fn-product-details-document-table :product="product" />

        <template v-if="language === 'fr'">
          <div class="pd-package">
            <img
              :alt="$t('product.triman.name')"
              :src="trimanLogo.src"
              :width="trimanLogo.width"
              :height="trimanLogo.height"
            />
            <p v-html="$t('product.triman.text')" />
          </div>
        </template>

        <template v-if="hasSizeTablesOrMeasureInfo">
          <fn-link
            class="pd-modal-sizes-and-measurements-link"
            no-arrow
            inline
            icon
            bold
            underline
            @click.prevent="$emit('modalSizesAndMeasurementsOpened')"
          >
            <fn-icon
              icon="ruler"
              color="black"
              size="xs"
            />
            {{ $t('product.sizesAndMeasurements.title') }}
          </fn-link>
        </template>
      </fn-accordion-item>

      <fn-accordion-item
        v-if="hasMaterialsOrCareInstructions"
        ref="detailedMaterialsAndCareInstructions"
        :headline="$t('product.materialsAndCareInstructions')"
        :active="activeTab === 'detailedMaterialsAndCareInstructions'"
        @toggle="changeTab($event, 'detailedMaterialsAndCareInstructions')"
      >
        <fn-product-materials-and-cleaning-recommendations
          :materials="productMaterials"
          :cleaning-recommendations="cleaningRecommendations"
          :material-warning="materialWarning"
        />
      </fn-accordion-item>

      <fn-accordion-item
        v-if="product.feedingSuggestions?.length"
        ref="detailedFeedingSuggestion"
        :headline="$t('product.feedingSuggestion')"
        :active="activeTab === 'detailedFeedingSuggestion'"
        @toggle="(active) => changeTab(active, 'detailedFeedingSuggestion')"
      >
        <div
          v-for="(feedingSuggestion, index) in product.feedingSuggestions"
          :key="index"
        >
          <div
            v-if="feedingSuggestion.description"
            class="feeding-suggestion"
            v-html="feedingSuggestion.description"
          />

          <fn-product-feeding-suggestion-table
            v-if="feedingSuggestion.table"
            :feeding-suggestion-table="feedingSuggestion.table"
          />
        </div>
      </fn-accordion-item>

      <fn-accordion-item
        v-if="product.nutritionalPurpose"
        ref="detailedNutritionalPurpose"
        :headline="$t('product.nutritionalPurpose')"
        :active="activeTab === 'detailedNutritionalPurpose'"
        @toggle="changeTab($event, 'detailedNutritionalPurpose')"
      >
        <div v-html="product.nutritionalPurpose" />
      </fn-accordion-item>

      <fn-accordion-item
        v-if="hasDetailedContentMaterials"
        ref="detailedContentMaterials"
        :headline="$t('product.contentMaterials')"
        :active="activeTab === 'detailedContentMaterials'"
        @toggle="changeTab($event, 'detailedContentMaterials')"
      >
        <div v-if="product.contentMaterials">
          <strong>{{ $t('product.analyticContentMaterials') }}</strong>
          <div v-html="product.contentMaterials" />
        </div>

        <div v-if="product.composition">
          <strong>{{ $t('product.composition') }}</strong>
          <div v-html="product.composition" />
        </div>

        <div v-if="product.moistureContent">
          <strong>{{ $t('product.moistureContent') }}</strong>
          <div
            class="paragraph"
            v-html="product.moistureContent"
          />
        </div>

        <div v-if="product.additives">
          <strong>{{ $t('product.additives') }}</strong>
          <div
            class="paragraph"
            v-html="product.additives"
          />
        </div>

        <div v-if="product.admrCompliance">
          <strong>{{ $t('product.admrCompliance') }}</strong>
          <div
            class="paragraph"
            v-html="product.admrCompliance"
          />
        </div>

        <div v-if="product.biocide">
          <strong>{{ $t('product.biocides.biocides') }}</strong>
          <div class="paragraph paragraph--multi-row">
            <span v-html="product.biocide.components" />
            <span v-html="product.biocide.biocidalProductType" />

            <div v-if="product.biocide.registrationNumber">
              <label>{{ $t('product.biocides.registrationNumber') }}</label>
              <span v-html="product.biocide.registrationNumber" />
            </div>

            <div v-if="product.biocide.pznNumber">
              <label>{{ $t('product.biocides.pzn') }}</label>
              <span v-html="product.biocide.pznNumber" />
            </div>

            <div v-if="product.biocide.ufiCode">
              <label>{{ $t('product.biocides.ufi') }}</label>
              <span v-html="product.biocide.ufiCode" />
            </div>
          </div>
        </div>
      </fn-accordion-item>

      <fn-accordion-item
        v-if="product.dosageRecommendation"
        ref="detailedDosageRecommendation"
        :headline="$t('product.dosageRecommendation')"
        :active="activeTab === 'detailedDosageRecommendation'"
        @toggle="changeTab($event, 'detailedDosageRecommendation')"
      >
        <div v-html="product.dosageRecommendation" />
      </fn-accordion-item>

      <fn-accordion-item
        ref="detailedShippingInformation"
        :headline="$t('product.shippingInformation')"
        :active="activeTab === 'detailedShippingInformation'"
        @toggle="changeTab($event, 'detailedShippingInformation')"
      >
        <div v-if="parcelShippingInformation">
          <div
            v-html="
              $t('shipping.deliveryTime.parcel', {
                minimumOrderValue: minimumOrderValue ? minimumOrderValue.formattedValue : '',
                shippingFee: shippingFee ? shippingFee.formattedValue : '',
                freeShippingThreshold: freeShippingThreshold ? freeShippingThreshold.formattedValue : '',
                min: product.shippingInformation.deliveryTimeMin,
                max: product.shippingInformation.deliveryTimeMax,
              }) + additionalShippingInformation
            "
          />
        </div>

        <div v-if="speditionShippingInformation">
          <div
            v-html="
              $t('shipping.deliveryTime.spedition', {
                minimumOrderValue: minimumOrderValue ? minimumOrderValue.formattedValue : '',
                shippingFee: shippingFee ? shippingFee.formattedValue : '',
                freeShippingThreshold: freeShippingThreshold ? freeShippingThreshold.formattedValue : '',
                min: product.shippingInformation.deliveryTimeMin,
                max: product.shippingInformation.deliveryTimeMax,
                extendedDeliveryTimeMin: product.shippingInformation.extendedDeliveryTimeMin,
                extendedDeliveryTimeMax: product.shippingInformation.extendedDeliveryTimeMax,
              }) + additionalShippingInformation
            "
          />
        </div>

        <div v-if="dropshipShippingInformation">
          <div
            v-html="
              $t('shipping.deliveryTime.dropship', {
                minimumOrderValue: minimumOrderValue ? minimumOrderValue.formattedValue : '',
                shippingFee: shippingFee ? shippingFee.formattedValue : '',
                freeShippingThreshold: freeShippingThreshold ? freeShippingThreshold.formattedValue : '',
                min: product.shippingInformation.deliveryTimeMin,
                max: product.shippingInformation.deliveryTimeMax,
                extendedDeliveryTimeMin: product.shippingInformation.extendedDeliveryTimeMin,
                extendedDeliveryTimeMax: product.shippingInformation.extendedDeliveryTimeMax,
              }) + additionalShippingInformation
            "
          />
        </div>

        <div v-if="frozenShippingInformation">
          <div class="pd-table">
            <div
              v-html="
                $t('shipping.deliveryTime.frozen', {
                  minimumOrderValue: minimumOrderValue ? minimumOrderValue.formattedValue : '',
                  shippingFee: shippingFee ? shippingFee.formattedValue : '',
                  freeShippingThreshold: freeShippingThreshold ? freeShippingThreshold.formattedValue : '',
                  min: product.shippingInformation.deliveryTimeMin,
                  max: product.shippingInformation.deliveryTimeMax,
                })
              "
            />
          </div>
        </div>
      </fn-accordion-item>

      <fn-accordion-item
        v-if="hasUnderstandProductText"
        ref="understandCustomerRating"
        :headline="$t('product.understandProductRatings.label')"
        :active="activeTab === 'understandCustomerRating'"
        @toggle="changeTab($event, 'understandCustomerRating')"
      >
        <div v-html="$t('product.understandProductRatings.productReviews')" />
        <div v-html="$t('product.understandProductRatings.handlingReviews')" />
      </fn-accordion-item>
    </fn-accordion>

    <fn-box
      v-if="showBiocidesBox"
      ref="biocidesBox"
      class="biocides-box"
      rounded
    >
      <div
        v-if="product.biocide.pictograms"
        class="bb-pictograms"
      >
        <img
          v-for="pictogram in product.biocide.pictograms"
          :key="pictogram.url"
          :src="pictogram.url"
          width="32"
          height="32"
        />
      </div>

      <fn-headline
        v-if="product.biocide.signalWord"
        no-margin-bottom
        type="h2"
        size="h5"
        color="grey-dark"
      >
        {{ product.biocide.signalWord }}
      </fn-headline>
      <p v-if="product.biocide.hazardDescriptions">
        {{ product.biocide.hazardDescriptions }}
      </p>
    </fn-box>
  </div>
</template>

<script>
import {
  SHIPPING_TYPE_DROPSHIP,
  SHIPPING_TYPE_FROZEN,
  SHIPPING_TYPE_PARCEL,
  SHIPPING_TYPE_SPEDITION,
} from '@/utils/shipping'; /* eslint-disable import/no-duplicates */
import TrimanLogo from '@/assets/img/triman-logo.svg';
import TrimanLogoSize from '@/assets/img/triman-logo.svg?imageSize'; /* eslint-enable import/no-duplicates */

export default defineComponent({
  props: {
    product: {
      type: Object,
      required: true,
    },
    accordionState: {
      type: Boolean,
    },
    activeTab: {
      type: String,
      default: null,
    },
  },
  emits: ['modalSizesAndMeasurementsOpened', 'changeTab', 'setActiveTab'],
  computed: {
    ...mapState(useMainStore, {
      configuration: 'configuration',
      shippingFee: 'getShippingFee',
      language: 'language',
      minimumOrderValue: 'getMinimumOrderValue',
      freeShippingThreshold: 'getFreeShippingThreshold',
      isMobileView: 'isMobileView',
    }),

    speditionShippingInformation() {
      return this.product.shippingInformation.detailedShippingInformation === SHIPPING_TYPE_SPEDITION;
    },
    parcelShippingInformation() {
      return this.product.shippingInformation.detailedShippingInformation === SHIPPING_TYPE_PARCEL;
    },
    dropshipShippingInformation() {
      return this.product.shippingInformation.detailedShippingInformation === SHIPPING_TYPE_DROPSHIP;
    },
    frozenShippingInformation() {
      return this.product.shippingInformation.detailedShippingInformation === SHIPPING_TYPE_FROZEN;
    },
    additionalShippingInformation() {
      return this.product.marketplaceArticle ? `${this.$t('shipping.deliveryTime.additionalInfo')}` : '';
    },
    showBiocidesBox() {
      const biocides = this.product.biocide;
      return biocides?.pictograms?.length || (biocides?.signalWord && biocides?.hazardDescriptions);
    },
    hasUnderstandProductText() {
      return (
        this.$te('product.understandProductRatings.label') &&
        this.$te('product.understandProductRatings.productReviews') &&
        this.$te('product.understandProductRatings.handlingReviews')
      );
    },
    trimanLogo() {
      return {
        src: TrimanLogo,
        ...TrimanLogoSize,
      };
    },
    hasDetailedContentMaterials() {
      return (
        this.product.contentMaterials ||
        this.product.moistureContent ||
        this.product.composition ||
        this.product.additives ||
        this.product.biocide
      );
    },
    productWarnings() {
      // Don't display the legacy biocide warning when the new biocide info is available
      if (this.product?.biocide?.noticeText) {
        return this.product.warnings.filter((warning) => warning !== 'contains_pesticides');
      }

      // Don't display the non_textile_parts_animal_origin warning when hasMaterialsOrCareInstructions is true
      // since warning will be displayed in Material & Cleaning recommendation section already
      if (this.hasMaterialsOrCareInstructions) {
        return this.product.warnings.filter((warning) => warning !== 'non_textile_parts_animal_origin');
      }

      return this.product.warnings;
    },

    plpBrandFullName() {
      const { brandName, title, variantName } = this.product;
      return [brandName, title, variantName].filter(Boolean).join(' ');
    },

    hasMaterialsOrCareInstructions() {
      return !!Object.keys(this.product?.material || {}).length || !!this.product?.cleaningRecommendations?.length;
    },

    hasSizeTablesOrMeasureInfo() {
      return (
        !!Object.keys(this.product?.measureInfo || {}).length ||
        !!this.product?.sizeTable?.table?.length ||
        !!this.product?.sizeTableMeasurement?.table?.length
      );
    },

    materialWarning() {
      const materialWaring = 'non_textile_parts_animal_origin';
      return this.product.warnings.find((material) => material === materialWaring);
    },

    productMaterials() {
      const {
        outerMaterial = '',
        clothingFillingMaterial = '',
        innerMaterial = '',
        materialConstruction = '',
        shoeInsole = '',
        shoeSole = '',
        closureType = '',
        clothingComposition = '',
        denier = '',
      } = this.product?.material || {};

      const materialsToRender = [
        {
          code: 'outerMaterial',
          text: outerMaterial,
        },
        {
          code: 'clothingFillingMaterial',
          text: clothingFillingMaterial,
        },
        {
          code: 'innerMaterial',
          text: innerMaterial,
        },
        {
          code: 'materialConstruction',
          text: materialConstruction,
        },
        {
          code: 'shoeInsole',
          text: shoeInsole,
        },
        {
          code: 'shoeSole',
          text: shoeSole,
        },
        {
          code: 'closureType',
          text: closureType,
        },
        {
          code: 'clothingComposition',
          text: clothingComposition,
        },
        {
          code: 'denier',
          text: denier,
        },
      ].filter((material) => material.text !== '');

      return materialsToRender;
    },

    cleaningRecommendations() {
      return this.product?.cleaningRecommendations;
    },
  },
  data: () => ({
    mobileActive: false,
  }),
  mounted() {
    if (import.meta.client && this.isMobileView && this.activeTab === null) {
      this.$emit('setActiveTab', 'detailedProductDetails');
      this.mobileActive = true;
    }
  },
  methods: {
    changeTab(active, tab) {
      this.$emit('changeTab', active ? tab : null);
    },
  },
});
</script>

<style lang="scss" scoped>
.product-details {
  :deep(.advantage-headline) {
    width: 100%;
    padding: $pad $pad $pad 30px;
    margin-bottom: 0;
    background-color: palette(sand, base);
    border-bottom: 1px solid $color-white;
  }

  :deep(ul:not(.pmacr-cleaning-recommendations)) {
    width: 100%;
    padding: $pad $pad $pad 55px;
    margin-top: 0;
    background-color: palette(sand, light);
    list-style-type: none;

    li {
      margin-bottom: 12px;

      &:before {
        display: inline-block;
        width: 24px;
        margin-left: -24px;
        content: '\e910';
        font-family: 'Icons';
        color: $color-brand-fnmz-green;
      }
    }
  }

  :deep(.link.link--inline-icon:not(.pd-modal-sizes-and-measurements-link)) {
    display: inline-block;
    padding: 5px;
    margin-left: -5px;
    margin-right: 5px;
    word-wrap: anywhere;

    &:hover {
      background-color: palette(sand, light);
      border-radius: 2px;
    }
  }

  .pd-table {
    border: 0;

    :deep(table) {
      width: 100% !important;

      th {
        padding: $pad;
        border: 1px solid $color-white;
        background-color: palette(sand, base);
        text-align: center;
      }

      td {
        padding: $pad;
        border: 1px solid $color-white;
        background-color: palette(sand, light);
        text-align: center;
      }
    }
  }

  .feeding-suggestion {
    margin-bottom: math.div($pad, 2);
  }

  .pd-package {
    margin-top: 1rem;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;

    img {
      max-width: 65px;
      height: auto;
      margin-right: 1rem;
    }
  }

  :deep(table) {
    border: none;
  }

  :deep(td) {
    border: none;
  }
}

.pd-modal-sizes-and-measurements-link {
  padding: $pad 0;
}

.paragraph {
  margin-bottom: $pad;

  &--multi-row {
    display: flex;
    flex-direction: column;
  }
}

.biocides-box {
  margin-top: $ui-height;
  scroll-margin: $ui-height;
  background-color: palette(grey, lighter);

  .bb-pictograms {
    display: flex;
    flex-direction: row;
    grid-gap: math.div($pad, 2);
    margin-bottom: $pad;

    img {
      width: 32px;
      height: 32px;
    }
  }
}
</style>
